import _ from "lodash"
import { useEffect, useCallback, useMemo } from "react"
import { Id, featureCollection } from "@turf/helpers"

import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import {
  buildCompositeId,
  getDuplexGeometryIdsByFeature,
  getLocationIdByFeature,
  getRelatedLocationIdsByFeature,
  getSuitablyValueBetweenBearings,
  SUFFIX_MARKER,
} from "components/IndoorMap"
import { IRoute } from "interfaces"

const DEFAULT_OPTIONS = {
  offset: { top: 0, left: 0, right: 0, bottom: 0 },
  enabled: true,
}

type Options = {
  enabled?: boolean
  offset?: {
    bottom?: number
    left?: number
    right?: number
    top?: number
  }
}

type UseStepByStepType = (
  route: IRoute | null,
  stepNumber: number,
  options: Options
) => void

export const useStepByStepMap: UseStepByStepType = (
  route,
  stepNumber,
  options = DEFAULT_OPTIONS
) => {
  const { indoorRef, mapLoaded } = useIndoorMap()
  const {
    amenities,
    dataLoaded,
    transformStepsToGeometries,
    transformLocationToFeature,
    relationshipGraphLoaded,
  } = useVenue()

  const { offset, enabled } = options
  const { bottom, top, left, right } = offset
  const offsetOption = useMemo(
    () => ({ bottom, top, left, right }),
    [bottom, top, left, right]
  )
  const getAmenityIdsFromUnitIds = useCallback(
    (unitIds: Id[]) => {
      const matchingAmenities = amenities.filter((amenity) =>
        amenity.properties?.unit_ids?.some((id) => unitIds.includes(id))
      )
      return matchingAmenities.map((amenity) => amenity.id)
    },
    [amenities]
  )
  const mapReady =
    indoorRef?.current && mapLoaded && dataLoaded && relationshipGraphLoaded

  const navigationGeometries = useMemo(
    () => (route?.steps ? transformStepsToGeometries(route?.steps) : []),
    [route?.steps, transformStepsToGeometries]
  )

  const targetDestination = useMemo(() => {
    const targetLocationId = _.get(
      route,
      "destination.properties.target_location_id"
    )
    return transformLocationToFeature(targetLocationId)
  }, [route, transformLocationToFeature])

  // Effect#1: Prepare indoor map before drawing route path
  useEffect(() => {
    const indoorMap = indoorRef?.current
    if (indoorMap && mapLoaded) {
      indoorMap.hideVenueObjects()
      indoorMap.disableClick()
      indoorMap.disableLayersFadingOnZoom()
      //set opacity of extrudedUnits to 0.75
      indoorMap?.setFeatureObject3DsOpacity(0.75)
    }

    return () => {
      if (indoorMap && mapLoaded) {
        indoorMap.enableLayersFadingOnZoom()
        indoorMap.setFeatureObject3DsOpacity(1)
        indoorMap.setSpriteMarkersOpacity(1)
      }
    }
  }, [indoorRef, mapLoaded])

  // Effect#2: Create new route path on indoor map
  useEffect(() => {
    if (mapReady && enabled && route) {
      indoorRef.current.clearNavigationGeometries()
      indoorRef.current.createNavigationGeometries(
        navigationGeometries,
        route.destination
      )
    }
  }, [indoorRef, navigationGeometries, mapReady, enabled, route])

  /**
   * Effect#3: Update view for the current navigation step:
   * - Highlight the destination unit if it exists.
   * - Highlight the vertical step marker.
   * - Move camera to the step's route path.
   * - Hide origin and destination logo / text marker
   */
  useEffect(() => {
    if (mapReady && enabled) {
      const indoorMap = indoorRef.current
      // handle the step page
      const targetStepGeoJSON = navigationGeometries.find(
        ({ id }) => id === `step-${stepNumber}`
      )

      if (!targetStepGeoJSON) return

      indoorRef.current.clearHighlightObject()
      indoorRef.current.clearHighlightElements()
      indoorRef.current.undoHiddenElements()

      const {
        properties,
        isVerticalStep,
        isLastStep,
        geometry,
        hasGeolocationOrigin,
      } = targetStepGeoJSON
      const { ordinal: currentStepOrdinal, intermediaryUnits } = properties

      //only show geometry, path and maker in current ordinal
      indoorRef.current.changeLevelByOrdinal(currentStepOrdinal)

      // Highlight the destination unit if the current floor step has a destination unit.
      const hasLocatedDestination =
        currentStepOrdinal === targetDestination.properties?.ordinal
      const ignoreTargetId =
        targetDestination?.feature_type &&
        ["geolocation", "occupant", "section"].includes(
          targetDestination?.feature_type
        )
      const ignoreRelatedLocation =
        targetDestination?.feature_type &&
        ["section"].includes(targetDestination?.feature_type)

      const relatedLocationIdsFromDestination = ignoreRelatedLocation
        ? []
        : getRelatedLocationIdsByFeature(targetDestination)
      const destinationToHighlightingId = ignoreRelatedLocation
        ? null
        : getLocationIdByFeature(targetDestination)

      const destElementIds =
        hasLocatedDestination &&
        _.compact(
          _.uniq([
            destinationToHighlightingId,
            ...relatedLocationIdsFromDestination,
            ignoreTargetId ? null : targetDestination?.id,
          ]) //Find new solution to refactor SetHighlight Element object
          // filter for prevent setHighlightedObject function create occupant highlight marker
        )

      if (destElementIds && destElementIds.length > 0) {
        indoorRef.current.setHighlightedObject(destElementIds)
      }

      // Highlight elevator, escalator, and stairs markers for vertical steps.
      if (isVerticalStep) {
        //verticalStep's intermediaries are elevators/escalators/stairs
        const amenitiesToHighlight = getAmenityIdsFromUnitIds(intermediaryUnits)
        indoorRef.current.setHighlightedObject(amenitiesToHighlight)
      }

      // If this is a vertical step or the last step, animate the camera to fly to and zoom in on the geometry.
      if (isVerticalStep || isLastStep) {
        indoorRef.current.flyToAndZoomIn(geometry.coordinates)
      }

      // Handle Zoom And Focusing
      const navigationCollection = featureCollection([targetStepGeoJSON])
      const extent = indoorRef.current.getFeatureExtent(navigationCollection)
      const center = indoorRef.current.getExtentCenter(extent)
      const zoom = indoorRef.current.getExtentZoom(extent)
      const pitch = 60
      const currentBearing = indoorRef.current.getBearing()
      const defaultBearing = indoorRef.current.defaultBearing
      const pathBearing =
        indoorRef.current.getLineStringBearing(targetStepGeoJSON)

      // If geolocation is available at the origin in this step, use the default bearing to set the map view instead of following the path's bearing.
      const targetBearing = hasGeolocationOrigin ? defaultBearing : pathBearing

      const bearing = getSuitablyValueBetweenBearings(
        targetBearing,
        currentBearing
      )

      const newCenter = indoorMap.getTargetViewCenter(
        {
          center,
          zoom,
          pitch,
          bearing,
        },
        { offset: offsetOption }
      )
      indoorRef.current.flyTo(newCenter, {
        zoom: zoom,
        bearing,
        pitch,
        duration: 600,
        easing: "out",
      })

      // TODO: improve logic to show origin logo marker for case both, occupant render_type === logo (mobile)
      // hide origin and destination logo / text marker prevent overlap marker
      const routeDestination = route?.destination
      const isDestinationSection = routeDestination?.feature_type === "section"
      const destinationMarkerId = isDestinationSection
        ? buildCompositeId([routeDestination?.id], {
            suffix: SUFFIX_MARKER,
          })
        : routeDestination?.id
      const destDuplexIds = getDuplexGeometryIdsByFeature(routeDestination)
      const destElementIdsToHide = _.compact([
        destinationMarkerId,
        ...destDuplexIds,
      ])
      indoorRef.current.hideRelatedHighlightElementByIds(destElementIdsToHide)

      const routeOrigin = route?.origin
      const isOriginSection = routeOrigin?.feature_type === "section"
      const originMarkerId = isOriginSection
        ? buildCompositeId([routeOrigin?.id], {
            suffix: SUFFIX_MARKER,
          })
        : routeOrigin?.id
      const originDuplexIds = getDuplexGeometryIdsByFeature(routeOrigin)
      const originElementIdsToHide = _.compact([
        originMarkerId,
        ...originDuplexIds,
      ])
      indoorRef.current.hideRelatedHighlightElementByIds(originElementIdsToHide)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    indoorRef,
    mapReady,
    navigationGeometries,
    offsetOption,
    targetDestination,
    route?.destination.id,
    route?.origin.id,
    stepNumber,
    getAmenityIdsFromUnitIds,
  ])
}
